import { createSlice } from '@reduxjs/toolkit';
import { MappingTable, MappingTableCell } from 'src/types/MappingInterface';
import {
  MAPPING_TABLE_MODE,
  MAPPING_TABLE_ORIENTATIONS,
  MAPPING_TABLE_STEPS,
} from './MappingTableSliceConstants';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';

import { AppDispatch } from 'src/redux/store';

export type RenderingMode = 'separate' | 'combine';

// One choice in the radio list
export interface RenderingModeChoice {
  value: RenderingMode;
  label: string;
}

export interface MappingTableState {
  tableMode: MAPPING_TABLE_MODE;
  currentStep: MAPPING_TABLE_STEPS;
  mappingTableModalSize: 'sm' | 'md' | 'lg' | 'xl';
  openMappingTableModal: boolean;
  isDrawingMappingTable: boolean;
  isEditingMappingTable: boolean;
  mappingOrientationChoices: [];
  mappingRenderingModeChoices: RenderingModeChoice[];

  mappingTableConfig: MappingTable;
  tableCellConfig: MappingTableCell;

  selectedGroupIds: number[];
  renderingMode: RenderingMode;
  formMappingTables: MappingTable[];
  formMappingTableCells: MappingTableCell[];
}

export const baseTableCellConfig = {
  id: null,
  qits: [],
  calculation: null,
  tic_bucket_calc: null,
  calculation_name: false,
  question_group_name: false,
  question_group_field: null,
  household_member_field: null,
  hhm_number: false,
  empty_render_value: '',
  text: '',
  render_type: 'TextRender',
  render_type_list: [],
  choices_custom_values: [],
  qits_choices: [],
};

export const baseMappingTableConfig: MappingTable = {
  id: null,
  form_page: 0,
  question_groups: [],
  member_types: [],
  row_overflow_threshold: 1,
  layout_config: [],
  mapping_orientation: MAPPING_TABLE_ORIENTATIONS.VERTICAL,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  table_cells: [],
};

const INITIAL_STATE: MappingTableState = {
  tableMode: MAPPING_TABLE_MODE.QUESTIONNAIRE,
  currentStep: MAPPING_TABLE_STEPS.CONFIGURE_MAPPING_TABLE,
  openMappingTableModal: false,
  mappingTableModalSize: 'sm',
  isDrawingMappingTable: false,
  isEditingMappingTable: false,
  mappingOrientationChoices: [],
  mappingRenderingModeChoices: [],

  mappingTableConfig: baseMappingTableConfig,
  tableCellConfig: baseTableCellConfig,

  selectedGroupIds: [],
  renderingMode: 'separate',
  formMappingTables: [],
  formMappingTableCells: [],
};

const slice = createSlice({
  name: 'MappingTable',
  initialState: INITIAL_STATE,
  reducers: {
    updateTableCellConfig: (state, action) => {
      const qit_ids =
        (action.payload?.qits || state?.tableCellConfig?.qits)?.map(
          (qit) => qit.id
        ) || [];

      let newRenderTypeList = [
        ...(action.payload?.render_type_list ||
          state.tableCellConfig.render_type_list ||
          []),
      ].map((item) => ({ ...item })); // deep list copy
      const { qit_id, render_type } = action.payload;
      if (qit_id) {
        const existing = newRenderTypeList.find(
          (qitObj) => qitObj.qit_id === qit_id
        );

        if (existing) {
          existing.render_type = render_type;
        } else {
          newRenderTypeList.push({ qit_id, render_type });
        }
      }
      newRenderTypeList = newRenderTypeList.filter((qitObj) =>
        qit_ids.includes(qitObj.qit_id)
      );
      const finalState = {
        ...state.tableCellConfig,
        ...action.payload,
      };
      if (newRenderTypeList) {
        finalState.render_type_list = newRenderTypeList;
      }
      state.tableCellConfig = finalState;
    },
    updateTableCellCalculationRenderType: (state, action) => {
      const render_type = action.payload;
      state.tableCellConfig = {
        ...state.tableCellConfig,
        render_type,
      };
    },
    onQuestionGroupSelected: (state, action) => {
      const selectedGroup = action.payload;
      const IsAdded = state.selectedGroupIds.some(
        (groupId) => groupId === selectedGroup.id
      );
      if (IsAdded) {
        state.selectedGroupIds = state.selectedGroupIds.filter(
          (groupId) => groupId !== selectedGroup.id
        );

        state.mappingTableConfig.question_groups =
          state.mappingTableConfig.question_groups.filter(
            (group) => group.id !== selectedGroup.id
          );
      } else {
        state.selectedGroupIds.push(selectedGroup.id);
        state.mappingTableConfig.question_groups.push(selectedGroup);
      }
    },
    onQuestionSelected: (state, action) => {
      const { qitID, question, boxChecked } = action.payload;

      let removeQITID;
      let newQITsChoices =
        state.tableCellConfig.qits_choices.map((qitChoice) => ({
          ...qitChoice,
        })) || [];
      let newQITs =
        state.tableCellConfig.qits.map((tempQIT) => ({ ...tempQIT })) || [];

      if (boxChecked) {
        newQITsChoices = [];
        removeQITID = qitID;
      } else {
        newQITs.push(question.qit);
      }

      newQITs = newQITs.filter(
        (tempQIT) =>
          tempQIT.question_group.id !== question.qit.question_group.id ||
          tempQIT.id === qitID
      );

      if (removeQITID) {
        newQITs = newQITs.filter((tempQIT) => tempQIT.id !== removeQITID);
      }

      const newQITIDs = newQITs?.map((tempQIT) => tempQIT.id);

      newQITsChoices = newQITsChoices.filter((qitChoice) =>
        newQITIDs.includes(qitChoice.qit)
      );

      state.tableCellConfig.qits = newQITs;
      state.tableCellConfig.qits_choices = newQITsChoices;
    },
    onQuestionChoiceSelected: (state, action) => {
      const qit = action.payload;
      if (!qit?.selectedChoice) {
        return;
      }

      const choiceID = qit.selectedChoice.id;
      const qitID = qit.id;
      const choiceToggle = !qit.choiceIsSelected;

      let newQITsChoices = (state.tableCellConfig.qits_choices || []).map(
        (qitChoices) => ({
          ...qitChoices,
        })
      );
      const qitChoices = newQITsChoices.find((c) => c.qit === qitID);

      if (qitChoices) {
        const currentChoices = qitChoices.choices || [];
        if (choiceToggle) {
          qitChoices.choices = [...currentChoices, choiceID];
        } else {
          qitChoices.choices = currentChoices.filter((id) => id !== choiceID);
        }
      } else {
        newQITsChoices.push({ qit: qitID, choices: [choiceID], blank: false });
      }

      if (qitChoices?.choices?.length === qit?.question_choices?.length) {
        qitChoices.choices = [];
      }

      newQITsChoices = newQITsChoices.filter(
        (qitChoices) => qitChoices.choices.length > 0
      );

      const newQITs = state.tableCellConfig.qits
        .map((tempQIT) => ({ ...tempQIT }))
        .filter(
          (tempQIT) => tempQIT.question_group.id !== qit.question_group.id
        );

      if (!newQITs.some((q) => q.id === qitID)) {
        newQITs.push(qit);
      }

      state.tableCellConfig.qits = newQITs;
      state.tableCellConfig.qits_choices = newQITsChoices;
    },
    onQITChoicesChangeBlank: (state, action) => {
      const { qit, value } = action.payload;
      const newQitsChoices = [
        ...state.tableCellConfig.qits_choices.map((qitChoices) => ({
          ...qitChoices,
        })),
      ];

      const qitChoices = newQitsChoices.find(
        (qitChoices) => qitChoices.qit === qit.id
      );
      qitChoices.blank = value;

      state.tableCellConfig.qits_choices = newQitsChoices;
    },
    onCalculationSelected: (state, action) => {
      const calculation = action.payload;
      state.tableCellConfig.calculation = calculation;
      state.tableCellConfig.text = calculation.name;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setFormMappingTables: (state, action) => {
      state.formMappingTables = action.payload;
    },
    addFormMappingTable: (state, action) => {
      state.formMappingTables.push(action.payload);
    },
    removeTemporalMappingTablesAndCells: (state) => {
      state.formMappingTables = state.formMappingTables.filter(
        (mappingTable) => typeof mappingTable.id === 'number'
      );
      state.formMappingTableCells = state.formMappingTableCells.filter(
        (cell) => typeof cell.id === 'number'
      );
    },
    setFormMappingTableCells: (state, action) => {
      state.formMappingTableCells = action.payload;
    },
    addOrUpdateFormMappingTableCell: (
      state: MappingTableState,
      action: PayloadAction<MappingTableCell>
    ) => {
      const newCell = action.payload;
      const existingIndex = state.formMappingTableCells.findIndex(
        (cell) => cell.id === newCell.id
      );

      if (existingIndex !== -1) {
        state.formMappingTableCells[existingIndex] = newCell;
      } else {
        state.formMappingTableCells.push(newCell);
      }
    },
    removeFormMappingTableCell: (
      state: MappingTableState,
      action: PayloadAction<string | number>
    ) => {
      const cellId = action.payload;
      state.formMappingTableCells = state.formMappingTableCells.filter(
        (cell) => cell.id !== cellId
      );
    },
    setIsDrawingMappingTable: (state, action) => {
      state.isDrawingMappingTable = action.payload;
    },
    setMappingTableConfigValues: setMappingTableConfigValuesReducer,
    setStateForEdit: (state, action) => {
      const { mappingTableConfig, tableMode, currentStep } = action.payload;

      state.currentStep = currentStep;
      state.selectedGroupIds = (mappingTableConfig.question_groups || []).map(
        (group) => group.id || group
      );
      state.mappingTableConfig = mappingTableConfig;
      state.tableMode = tableMode;
      state.renderingMode = mappingTableConfig.rendering_mode || 'separate';
      state.openMappingTableModal = true;
      state.isEditingMappingTable = true;
    },
    setTableMode: (state, action) => {
      state.tableMode = action.payload;
    },
    toggleMappingTableModal(state) {
      state.openMappingTableModal = !state.openMappingTableModal;
    },
    updateFormMappingTable: (state, action) => {
      const mappingTableData = action.payload;
      const mappingTableIndex = state.formMappingTables.findIndex(
        (mappingTable) => mappingTable.id === mappingTableData.id
      );
      if (mappingTableIndex !== -1) {
        state.formMappingTables[mappingTableIndex] = mappingTableData;
      } else {
        state.formMappingTables.push(mappingTableData);
      }
    },
    changeMappingTableModalSize(state, action) {
      state.mappingTableModalSize = action.payload;
    },
    resetTableCellConfig: (state) => {
      state.tableCellConfig = baseTableCellConfig;
    },
    clearMappingTableModalData: (state) => {
      let initialState = INITIAL_STATE;
      initialState = {
        ...initialState,
        formMappingTables: state.formMappingTables,
        formMappingTableCells: state.formMappingTableCells,
        mappingOrientationChoices: state.mappingOrientationChoices,
        mappingRenderingModeChoices: state.mappingRenderingModeChoices,
      };

      return initialState;
    },
    setMappingOrientationChoices: (state, action) => {
      state.mappingOrientationChoices = action.payload;
    },
    setMappingRenderingModeChoices: (
      state,
      action: PayloadAction<RenderingModeChoice[]>
    ) => {
      state.mappingRenderingModeChoices = action.payload;
    },
    setRenderingMode: (state, action: PayloadAction<RenderingMode>) => {
      state.renderingMode = action.payload;
    },
  },
});

export default slice.reducer;

export const {
  updateTableCellConfig,
  updateTableCellCalculationRenderType,
  onQuestionGroupSelected,
  onQuestionSelected,
  onQuestionChoiceSelected,
  onQITChoicesChangeBlank,
  onCalculationSelected,
  setCurrentStep,
  addFormMappingTable,
  setFormMappingTables,
  removeTemporalMappingTablesAndCells,
  setFormMappingTableCells,
  addOrUpdateFormMappingTableCell,
  removeFormMappingTableCell,
  setIsDrawingMappingTable,
  setMappingTableConfigValues,
  setStateForEdit,
  setTableMode,
  toggleMappingTableModal,
  changeMappingTableModalSize,
  updateFormMappingTable,
  resetTableCellConfig,
  clearMappingTableModalData,
  setMappingOrientationChoices,
  setMappingRenderingModeChoices,
  setRenderingMode,
} = slice.actions;

function setMappingTableConfigValuesReducer<K extends keyof MappingTable>(
  state: MappingTableState,
  action: PayloadAction<{ key: K; value: MappingTable[K] }>
): void {
  const { key, value } = action.payload;
  state.mappingTableConfig[key] = value;
  if (key === 'question_groups') {
    state.selectedGroupIds = (value as MappingTable['question_groups']).map(
      (group) => group.id
    );
  }
}

export function getMappingOrientationChoices() {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await axios.get(
        'mapping_question/mapping_orientation_choices'
      );
      dispatch(setMappingOrientationChoices(data));
    } catch (_error) {
      console.error('Error loading orientation choices', _error);
      dispatch(setMappingOrientationChoices([]));
    }
  };
}

export function getMappingRenderingModeChoices() {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await axios.get(
        'mapping_question/mapping_rendering_mode_choices'
      );
      dispatch(setMappingRenderingModeChoices(data));
    } catch (_error) {
      console.error('Error loading rendering mode choices', _error);
      dispatch(setMappingRenderingModeChoices([]));
    }
  };
}
